import { useContext, useEffect, useState } from 'react'
import {
  Box,
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalContent,
  ModalOverlay,
  Text,
} from '@chakra-ui/react'
import { Capacitor } from '@capacitor/core'
import { App } from '@capacitor/app'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { AuthContext } from '../../App'
import { getHealthEvents } from '../../api/user/profile/Profile'
import { requestHealthKitAuthorization } from '../../hooks/useHealthKitSync'
import api from '../../api/api'

const HealthKitPromptModal = () => {
  const { user } = useContext(AuthContext)
  const queryClient = useQueryClient()
  const [isOpen, setIsOpen] = useState(false)
  const [hasTriggered, setHasTriggered] = useState(false)

  const isNative = Capacitor.isNativePlatform()

  const healthkitConnected = user?.member_profile?.healthkit_connected === true
  const healthkitPrompted = user?.member_profile?.healthkit_prompted === true

  const recentEvents = useQuery(
    ['recentPastEvents'],
    () => getHealthEvents({ has_healthkit_data: false, page_size: 1 }),
    {
      enabled: isNative && !healthkitConnected && !healthkitPrompted,
      refetchOnWindowFocus: false,
    }
  )

  const updateProfile = useMutation(
    (data: { healthkit_connected?: boolean; healthkit_prompted?: boolean }) =>
      api.put('member/myprofile/', data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['auth'])
      },
    }
  )

  const checkAndShow = () => {
    if (!isNative || healthkitConnected || healthkitPrompted || hasTriggered) return

    const events = recentEvents?.data?.data
    if (!events?.length) return

    const now = new Date()
    const twentyFourHoursAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000)

    const hasRecentEndedEvent = events.some((event: any) => {
      const endTime = new Date(event.end_time)
      return endTime < now && endTime > twentyFourHoursAgo
    })

    if (hasRecentEndedEvent) {
      setHasTriggered(true) // Prevent duplicate mutations from effect re-runs
      updateProfile.mutate({ healthkit_prompted: true })
      setIsOpen(true)
    }
  }

  useEffect(() => {
    if (!isNative || healthkitConnected || healthkitPrompted) return

    checkAndShow()

    const listener = App.addListener('appStateChange', (state) => {
      if (state.isActive) {
        recentEvents.refetch()
      }
    })

    return () => {
      listener.then((l) => l.remove())
    }
  }, [isNative, healthkitConnected, healthkitPrompted, recentEvents.data])

  const handleConnect = async () => {
    const success = await requestHealthKitAuthorization()
    if (success) {
      updateProfile.mutate({ healthkit_connected: true })
    }
    setIsOpen(false)
  }

  const handleDismiss = () => {
    setIsOpen(false)
  }

  if (!isNative || healthkitConnected || healthkitPrompted) return null

  return (
    <Modal isOpen={isOpen} onClose={handleDismiss} isCentered>
      <ModalOverlay />
      <ModalContent borderRadius="16px" mx="20px" p="8px">
        <ModalBody textAlign="center" py="24px">
          <Text fontSize="36px" mb="8px">
            🏅
          </Text>
          <Text fontSize="18px" fontWeight="700" mb="4px">
            Great game!
          </Text>
          <Text color="gray.500" fontSize="13px" mb="20px">
            Connect Health Tracking to see your stats from today's event
          </Text>

          <Flex
            bg="gray.50"
            borderRadius="12px"
            p="16px"
            mb="20px"
            justify="space-around"
          >
            <Box textAlign="center">
              <Text fontSize="11px" color="gray.400" textTransform="uppercase">
                Calories
              </Text>
              <Text fontSize="20px" fontWeight="700" color="gray.300">
                —
              </Text>
            </Box>
            <Box textAlign="center">
              <Text fontSize="11px" color="gray.400" textTransform="uppercase">
                Steps
              </Text>
              <Text fontSize="20px" fontWeight="700" color="gray.300">
                —
              </Text>
            </Box>
            <Box textAlign="center">
              <Text fontSize="11px" color="gray.400" textTransform="uppercase">
                Distance
              </Text>
              <Text fontSize="20px" fontWeight="700" color="gray.300">
                —
              </Text>
            </Box>
          </Flex>

          <Button
            w="100%"
            bg="black"
            color="white"
            borderRadius="10px"
            fontSize="15px"
            fontWeight="600"
            py="12px"
            mb="8px"
            _hover={{ bg: 'gray.800' }}
            onClick={handleConnect}
            isLoading={updateProfile.isLoading}
          >
            Connect Health Tracking
          </Button>
          <Button
            w="100%"
            variant="ghost"
            color="gray.400"
            fontSize="13px"
            onClick={handleDismiss}
          >
            Not now
          </Button>
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}

export default HealthKitPromptModal
